import { useState } from 'react';

import { useAppDispatch } from '../../../../app/redux/hooks';
import {
  Customer,
  ClaimCardRequest,
  ClaimCardResponse,
  useClaimCardMutation,
  useReplaceCardMutation,
  useLazyGetCustomerQuery,
} from '../../../api';
import { showErrorDialog } from '../../../error-dialog/errorDialogSlice';

import ClaimCardKeyboard from './ClaimCardKeyboard';

import './loyalty.sass';

interface ClaimCardModalProps {
  customer: Customer;
  loyaltyGuid: string;
  onClaimed: (updatedCustomer: Customer) => void;
  onSkip: () => void;
  onCancel: () => void;
}

type InputField = 'firstName' | 'lastName' | 'mobileNumber' | 'confirm';

function ClaimCardModal({ customer, loyaltyGuid, onClaimed, onSkip, onCancel }: ClaimCardModalProps) {
  const dispatch = useAppDispatch();
  const [claimCard, { isLoading }] = useClaimCardMutation();
  const [replaceCard, { isLoading: isReplacingCard }] = useReplaceCardMutation();
  const [getCustomer] = useLazyGetCustomerQuery();

  const [formData, setFormData] = useState<ClaimCardRequest>({
    firstName: '',
    lastName: '',
    mobileNumber: '',
  });

  const [currentField, setCurrentField] = useState<InputField>('firstName');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [conflictResponse, setConflictResponse] = useState<ClaimCardResponse | null>(null);

  const handleFieldChange = (value: string) => {
    setFormData({ ...formData, [currentField]: value });
    setValidationError(null);
  };

  const handleNext = () => {
    if (currentField === 'firstName') {
      if (!formData.firstName.trim()) {
        setValidationError('First name is required');
        return;
      }
      setCurrentField('lastName');
    } else if (currentField === 'lastName') {
      if (!formData.lastName.trim()) {
        setValidationError('Last name is required');
        return;
      }
      setCurrentField('mobileNumber');
    } else if (currentField === 'mobileNumber') {
      if (!formData.mobileNumber.trim()) {
        setValidationError('Mobile number is required');
        return;
      }
      setCurrentField('confirm');
    }
  };

  const handleBack = () => {
    if (currentField === 'lastName') setCurrentField('firstName');
    else if (currentField === 'mobileNumber') setCurrentField('lastName');
    else if (currentField === 'confirm') setCurrentField('mobileNumber');
  };

  const handleSubmit = async () => {
    // Validate
    if (!formData.firstName.trim()) {
      setValidationError('First name is required');
      setCurrentField('firstName');
      return;
    }

    if (!formData.lastName.trim()) {
      setValidationError('Last name is required');
      setCurrentField('lastName');
      return;
    }

    if (!formData.mobileNumber.trim()) {
      setValidationError('Mobile number is required');
      setCurrentField('mobileNumber');
      return;
    }

    setValidationError(null);

    try {
      // Call API to claim card
      const result = await claimCard({
        contactId: customer.id,
        request: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          mobileNumber: formData.mobileNumber.trim(),
        },
      }).unwrap();

      // Check if we got a conflict response (200 OK with data)
      if (result && 'exists' in result) {
        setConflictResponse(result);
        return;
      }

      // 204 No Content - Success, no conflict
      const updatedCustomer = await getCustomer(loyaltyGuid).unwrap();
      onClaimed(updatedCustomer);
    } catch (err: unknown) {
      console.error('Failed to claim card:', err);

      const error = err as { status?: number };
      if (error?.status === 404) {
        dispatch(
          showErrorDialog({
            dismissible: true,
            message: 'Card not found or cannot be claimed',
          }),
        );
      } else if (error?.status === 400) {
        dispatch(
          showErrorDialog({
            dismissible: true,
            message: 'Invalid information provided',
          }),
        );
      } else {
        dispatch(
          showErrorDialog({
            dismissible: true,
            message: 'Failed to claim card. Please try again.',
          }),
        );
      }
    }
  };

  const handleReplaceCard = async () => {
    if (!conflictResponse) return;

    try {
      await replaceCard({
        newCardId: customer.id,
        oldCardId: conflictResponse.existingContactId,
      }).unwrap();

      // Fetch updated customer data
      const updatedCustomer = await getCustomer(loyaltyGuid).unwrap();
      onClaimed(updatedCustomer);
    } catch (err: unknown) {
      console.error('Failed to replace card:', err);

      const error = err as { status?: number; data?: { detail?: string } };
      if (error?.status === 404) {
        dispatch(
          showErrorDialog({
            dismissible: true,
            message: 'Card not found',
          }),
        );
      } else if (error?.status === 400) {
        // Try to get the validation error message
        const errorMessage =
          (error as { data?: { detail?: string } })?.data?.detail || 'Card replacement failed. Please check the card status.';
        dispatch(
          showErrorDialog({
            dismissible: true,
            message: errorMessage,
          }),
        );
      } else {
        dispatch(
          showErrorDialog({
            dismissible: true,
            message: 'Failed to replace card. Please try again.',
          }),
        );
      }
    }
  };

  const getCurrentValue = () => {
    if (currentField === 'firstName') return formData.firstName;
    if (currentField === 'lastName') return formData.lastName;
    if (currentField === 'mobileNumber') return formData.mobileNumber || '';
    return '';
  };

  const getKeyboardMode = (): 'text' | 'phone' => {
    return currentField === 'mobileNumber' ? 'phone' : 'text';
  };

  return (
    <div className="pos-modal-portal">
      <div className="pos-modal-overlay">
        <div className="pos-modal pos-modal_role_servers-overview">
          <div className="pos-modal__content">
            <div className="pos-servers-overview">
              <div className="pos-servers-overview__header">
                <h2 className="pos-servers-overview__title">Claim Physical Card</h2>
              </div>

              <div className="loyalty-photo-verification">
                {conflictResponse && (
                  <>
                    <div
                      style={{
                        padding: '20px',
                        backgroundColor: conflictResponse.supportsReplacement ? '#fff3cd' : '#f8d7da',
                        border: `2px solid ${conflictResponse.supportsReplacement ? '#ffc107' : '#dc3545'}`,
                        borderRadius: '8px',
                        marginBottom: '20px',
                      }}
                    >
                      <h3 style={{ marginBottom: '10px', fontSize: '18px', fontWeight: 'bold' }}>
                        {conflictResponse.supportsReplacement ? 'Card Replacement Available' : 'Cannot Claim Card'}
                      </h3>
                      <p style={{ fontSize: '16px', marginBottom: '15px' }}>{conflictResponse.message}</p>

                      {conflictResponse.supportsReplacement && conflictResponse.isPhysicalCard && (
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                          This mobile number is already registered to another physical card. You can replace the old card with this new one.
                          All customer data, points, and history will be preserved.
                        </p>
                      )}

                      {!conflictResponse.supportsReplacement && (
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                          This mobile number is already registered to an online account. Card replacement is not supported for online
                          accounts.
                        </p>
                      )}
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        justifyContent: 'center',
                      }}
                    >
                      <button type="button" onClick={onCancel} disabled={isReplacingCard} className="pos-bookings__action">
                        Cancel
                      </button>
                      {conflictResponse.supportsReplacement && (
                        <button
                          type="button"
                          onClick={handleReplaceCard}
                          disabled={isReplacingCard}
                          className="pos-bookings__action"
                          style={{ backgroundColor: '#ffc107' }}
                        >
                          {isReplacingCard ? 'Replacing Card...' : 'Replace Card'}
                        </button>
                      )}
                    </div>
                  </>
                )}

                {!conflictResponse && currentField !== 'confirm' && (
                  <>
                    <p style={{ marginBottom: '10px', fontSize: '14px' }}>Unclaimed card - Enter customer details:</p>

                    <div style={{ marginBottom: '10px' }}>
                      {currentField === 'firstName' && (
                        <>
                          <label style={{ display: 'block', marginBottom: '5px', fontSize: '16px', fontWeight: 'bold' }}>
                            First Name *
                          </label>
                          <div
                            style={{
                              padding: '10px',
                              fontSize: '20px',
                              border: '2px solid #4CAF50',
                              borderRadius: '4px',
                              backgroundColor: '#fff',
                              minHeight: '40px',
                              textAlign: 'center',
                            }}
                          >
                            {formData.firstName || <span style={{ color: '#999' }}>Enter first name</span>}
                          </div>
                        </>
                      )}

                      {currentField === 'lastName' && (
                        <>
                          <label style={{ display: 'block', marginBottom: '5px', fontSize: '16px', fontWeight: 'bold' }}>Last Name *</label>
                          <div
                            style={{
                              padding: '10px',
                              fontSize: '20px',
                              border: '2px solid #4CAF50',
                              borderRadius: '4px',
                              backgroundColor: '#fff',
                              minHeight: '40px',
                              textAlign: 'center',
                            }}
                          >
                            {formData.lastName || <span style={{ color: '#999' }}>Enter last name</span>}
                          </div>
                        </>
                      )}

                      {currentField === 'mobileNumber' && (
                        <>
                          <label style={{ display: 'block', marginBottom: '5px', fontSize: '16px', fontWeight: 'bold' }}>
                            Mobile Number *
                          </label>
                          <div
                            style={{
                              padding: '10px',
                              fontSize: '20px',
                              border: '2px solid #4CAF50',
                              borderRadius: '4px',
                              backgroundColor: '#fff',
                              minHeight: '40px',
                              textAlign: 'center',
                            }}
                          >
                            {formData.mobileNumber || <span style={{ color: '#999' }}>+1234567890</span>}
                          </div>
                        </>
                      )}
                    </div>

                    {validationError && (
                      <div style={{ color: 'red', marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' }}>{validationError}</div>
                    )}

                    <ClaimCardKeyboard value={getCurrentValue()} onChange={handleFieldChange} mode={getKeyboardMode()} />

                    <div
                      style={{
                        marginTop: '10px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        justifyContent: 'center',
                      }}
                    >
                      {currentField !== 'firstName' && (
                        <button type="button" onClick={handleBack} disabled={isLoading} className="pos-bookings__action">
                          Back
                        </button>
                      )}
                      <button type="button" onClick={onCancel} disabled={isLoading} className="pos-bookings__action">
                        Cancel
                      </button>
                      <button type="button" onClick={handleNext} disabled={isLoading} className="pos-bookings__action">
                        {currentField === 'mobileNumber' ? 'Review' : 'Next'}
                      </button>
                    </div>
                  </>
                )}

                {!conflictResponse && currentField === 'confirm' && (
                  <>
                    <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>Confirm Details</p>

                    <div style={{ marginBottom: '15px', textAlign: 'left' }}>
                      <div style={{ marginBottom: '10px' }}>
                        <strong style={{ fontSize: '14px' }}>First Name:</strong>
                        <div style={{ fontSize: '18px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                          {formData.firstName}
                        </div>
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <strong style={{ fontSize: '14px' }}>Last Name:</strong>
                        <div style={{ fontSize: '18px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                          {formData.lastName}
                        </div>
                      </div>

                      {formData.mobileNumber && (
                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ fontSize: '14px' }}>Mobile Number:</strong>
                          <div style={{ fontSize: '18px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                            {formData.mobileNumber}
                          </div>
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: '10px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        justifyContent: 'center',
                      }}
                    >
                      <button type="button" onClick={handleBack} disabled={isLoading} className="pos-bookings__action">
                        Back
                      </button>
                      <button type="button" onClick={onSkip} disabled={isLoading} className="pos-bookings__action">
                        Skip
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="pos-bookings__action"
                        style={{ backgroundColor: '#4CAF50' }}
                      >
                        {isLoading ? 'Claiming...' : 'Claim Card'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClaimCardModal;
